<template>
  <div>
    <v-layout wrap row fill-height>
      <v-flex xs12 class="pa-2">
        <v-dialog v-model="show" width="80%" persistent>
          <template v-slot:activator="{ on }">
              <v-text-field v-on="on"
                            prepend-icon="event"
                            :append-icon="clearIcon"
                            placeholder="No login window has yet been selected"
                            :value="fieldText"
                            readonly
                            :disabled="$attrs.disabled"
                            @click:append="clear"
                            data-test="assessmentDateField"/>
          </template>
          <v-card>
            <v-card-title class="headline grey lighten-2" primary-title>
              Override Login Window
            </v-card-title>
            <slots-calendar
              v-model="localModel"
              :show="show"
              :initialTimezone="timezone"
              :requiredSlotsCount="requiredSlotsCount"
              @update:timezone="updateTimezone"
              :location="location"
              ref="slotsCalendar"
              selecting-empty-slots></slots-calendar>
            <v-card-actions>
              <v-spacer/>
              <v-btn @click="show = false" data-test="cancelAssessmentDate" text>Cancel</v-btn>
              <v-btn color="success darken-2" @click="save" data-test="saveAssessmentDate">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>
    <confirm-dialog ref="confirm" @confirm="model = ''" data-test="loginWindowConfirm">
      <p class="font-weight-bold">This will override the currently chosen login window.</p>
      <p>Are you sure?</p>
    </confirm-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import ModelBinding from '@/mixins/ModelBinding'
import ConfirmDialog from '@/components/ConfirmDialog'
import RequestCancelToken from '@/mixins/RequestCancelToken'
import { timezones, getDefaultTimezoneIndex, datetimeForHumans } from '@/services/time'
import SlotsCalendar from '@/components/SlotsCalendar'

export default {
  name: 'AssessmentDateField',
  mixins: [ ModelBinding, RequestCancelToken ],
  components: { SlotsCalendar, ConfirmDialog },
  props: {
    /**
     * Number of required time slots
     */
    requiredSlotsCount: {
      type: Number,
      required: false,
      default: 1
    },
    /**
     * Location to search for the timezone
     */
    location: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      show: false,
      loading: false,
      slots: [],
      now: moment(),
      timezone: getDefaultTimezoneIndex(),
      timezones,
      localModel: null,
      localTimezone: getDefaultTimezoneIndex()
    }
  },
  computed: {
    /**
     * Calendar component ref
     */
    calendarRef () {
      return `calendar-${this._uid}`
    },
    /**
     * Input field text
     */
    fieldText () {
      return (this.model === null || this.model === '')
        ? ''
        : `Current login window ${this.timezonedSlotForHumans} - ${this.selectedTimezone.label}`
    },
    /**
     * The selected timezone to query its available time slots
     */
    selectedTimezone () {
      return this.timezones[this.timezone]
    },
    /**
     * Display due date for users
     */
    timezonedSlotForHumans () {
      return (this.model)
        ? moment.utc(this.model).tz(this.selectedTimezone.name).format(datetimeForHumans)
        : '-'
    },
    /**
     * To show clear icon if an assessment date has been selected
     */
    clearIcon () {
      return this.model === null
        ? null
        : 'close'
    }
  },
  watch: {
    /**
     * Update local values when launching time slots dialog
     */
    show: {
      handler () {
        if (this.show) {
          this.localModel = this.model
          this.localTimezone = this.timezone
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Clear assessment date choice
     */
    clear () {
      this.$refs.confirm.shown = true
    },
    /**
     * Save selected timeslot
     */
    save () {
      this.$set(this, 'model', this.localModel)
      this.$set(this, 'timezone', this.localTimezone)
      this.$set(this, 'show', false)
    },
    /**
     * Update localTimezone value to match the selected on from slots calendar
     */
    updateTimezone (timezone) {
      this.localTimezone = timezone
    }
  }
}
</script>
